import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2, ShieldCheck } from "lucide-react";

const CERT_OPTIONS = ["8(a)", "HUBZone", "SDVOSB", "WOSB", "EDWOSB", "VOSB", "SDB", "Native American Owned", "Alaska Native Corporation"];
const INDUSTRY_OPTIONS = ["Roofing", "General Contracting", "Electrical", "HVAC", "Plumbing", "Concrete", "Civil", "Janitorial", "Landscaping", "Security", "IT Services", "Other"];
const ROLE_OPTIONS = [
  "Project Manager",
  "Superintendent",
  "SSHO (Site Safety & Health Officer)",
  "QCM (Quality Control Manager)",
  "Foreman",
  "Estimator",
  "Contract Administrator",
];

function TokenList({ label, items, onAdd, onRemove, placeholder, testid }) {
  const [val, setVal] = useState("");
  return (
    <div>
      <div className="mono-label text-slate-400 mb-2">{label}</div>
      <div className="flex gap-2 mb-2">
        <input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (val) { onAdd(val); setVal(""); } } }} placeholder={placeholder} className="flex-1 bg-[#0D1424] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid={`${testid}-input`} />
        <button type="button" onClick={() => { if (val) { onAdd(val); setVal(""); } }} className="border border-white/20 hover:border-cyan-400 hover:text-cyan-400 px-3 mono-label transition-colors" data-testid={`${testid}-add`}>Add</button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it, i) => (
          <span key={i} className="mono-label text-cyan-400 border border-cyan-400/30 px-2 py-1 flex items-center gap-1.5">
            {it}
            <button type="button" onClick={() => onRemove(i)} className="hover:text-red-400" data-testid={`${testid}-remove-${i}`}>×</button>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function CompanyProfile() {
  const [profile, setProfile] = useState({
    company_name: "", uei: "", duns: "", address: "", website: "", industry: "",
    naics_codes: [], psc_codes: [], certifications: [], bonding_capacity: "", insurance: "",
    service_areas: [], labor_force: 0, equipment: [], capabilities: "", key_personnel: [], past_performance: [], safety_record: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/company/profile").then(r => setProfile({ ...profile, ...r.data })).catch(() => {}); /* eslint-disable-next-line */ }, []);
  const set = (k, v) => setProfile(p => ({ ...p, [k]: v }));
  const addTo = (k, v) => setProfile(p => ({ ...p, [k]: [...p[k], v] }));
  const removeFrom = (k, i) => setProfile(p => ({ ...p, [k]: p[k].filter((_, idx) => idx !== i) }));
  const toggleCert = (c) => setProfile(p => ({ ...p, certifications: p.certifications.includes(c) ? p.certifications.filter(x => x !== c) : [...p.certifications, c] }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/company/profile", profile);
      toast.success("Company profile saved");
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-8 stagger-in max-w-5xl" data-testid="company-profile-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="mono-label text-cyan-400 mb-2">// KNOWLEDGE FOUNDATION</div>
          <h1 className="font-display font-black text-4xl tracking-tight">Company Profile</h1>
          <p className="text-slate-400 mt-2">The AI uses this to score, analyze, and draft.</p>
        </div>
        <button onClick={save} disabled={saving} className="bg-cyan-400 text-[#070B14] px-5 py-2.5 mono-label hover:bg-cyan-300 transition-colors flex items-center gap-2 disabled:opacity-50" data-testid="save-profile">
          <Save className="w-4 h-4" />{saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="space-y-6">
        <Section title="Company Info">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Company Name" value={profile.company_name} onChange={(v) => set("company_name", v)} testid="input-company-name" />
            <div>
              <div className="mono-label text-slate-400 mb-2">Industry</div>
              <select value={profile.industry || ""} onChange={(e) => set("industry", e.target.value)} className="w-full bg-[#0D1424] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="input-industry">
                <option value="">Select…</option>
                {INDUSTRY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <Field label="UEI" value={profile.uei} onChange={(v) => set("uei", v)} testid="input-uei" />
            <Field label="DUNS (optional)" value={profile.duns} onChange={(v) => set("duns", v)} testid="input-duns" />
            <Field label="Address" value={profile.address} onChange={(v) => set("address", v)} testid="input-address" />
            <Field label="Website" value={profile.website} onChange={(v) => set("website", v)} testid="input-website" />
          </div>
        </Section>

        <Section title="Codes & Certifications">
          <div className="grid md:grid-cols-2 gap-6">
            <TokenList label="NAICS Codes" items={profile.naics_codes} onAdd={(v) => addTo("naics_codes", v)} onRemove={(i) => removeFrom("naics_codes", i)} placeholder="e.g. 238160" testid="naics" />
            <TokenList label="PSC Codes" items={profile.psc_codes} onAdd={(v) => addTo("psc_codes", v)} onRemove={(i) => removeFrom("psc_codes", i)} placeholder="e.g. Y1BZ" testid="psc" />
          </div>
          <div className="mt-6">
            <div className="mono-label text-slate-400 mb-2">Business Certifications</div>
            <div className="flex flex-wrap gap-2">
              {CERT_OPTIONS.map(c => (
                <button key={c} type="button" onClick={() => toggleCert(c)} className={`mono-label px-3 py-1.5 border transition-colors ${profile.certifications.includes(c) ? "border-cyan-400 text-cyan-400 bg-cyan-400/10" : "border-white/10 text-slate-400 hover:border-white/30"}`} data-testid={`cert-${c}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
        </Section>

        <Section title="Capacity">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Bonding Capacity" value={profile.bonding_capacity} onChange={(v) => set("bonding_capacity", v)} placeholder="e.g. $5M single / $15M aggregate" testid="input-bonding" />
            <div>
              <div className="mono-label text-slate-400 mb-2">Surety Rate (%) — flat premium rate</div>
              <input
                type="number"
                step="0.01"
                min="0"
                max="15"
                value={profile.surety_rate_pct ?? ""}
                onChange={(e) => set("surety_rate_pct", e.target.value === "" ? null : Number(e.target.value))}
                placeholder="e.g. 3.0"
                className="w-full bg-[#0D1424] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none"
                data-testid="input-surety-rate"
              />
              <div className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                Used by <span className="font-mono">Reconcile Totals</span> to price Performance + Payment bond lines with the gross-up formula <span className="font-mono">base × rate / (1 − rate)</span> so a "3% of contract value" line is literally 3% of the printed total. Leave blank to skip auto-pricing.
              </div>
            </div>
            <Field label="Insurance" value={profile.insurance} onChange={(v) => set("insurance", v)} placeholder="e.g. $2M General Liability" testid="input-insurance" />
            <div>
              <div className="mono-label text-slate-400 mb-2">Labor Force (headcount)</div>
              <input type="number" value={profile.labor_force || 0} onChange={(e) => set("labor_force", Number(e.target.value))} className="w-full bg-[#0D1424] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="input-labor" />
            </div>
            <Field label="Safety Record" value={profile.safety_record} onChange={(v) => set("safety_record", v)} placeholder="EMR, recent incidents…" testid="input-safety" />
            <div>
              <div className="mono-label text-slate-400 mb-2">Bid Guarantee Pricing</div>
              <label className="flex items-start gap-2 text-sm text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!profile.price_bid_guarantee_by_default}
                  onChange={(e) => set("price_bid_guarantee_by_default", e.target.checked)}
                  className="mt-1 accent-cyan-400"
                  data-testid="input-price-bid-guarantee"
                />
                <span>
                  <span className="text-white">Price bid guarantee into each bid</span>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Off by default. A bid guarantee is a deliverable (surety-issued bond letter OR certified cheque), not a project cost — the surety-facility fee is usually amortised annually. Flip on ONLY if your surety invoices per project.
                  </div>
                </span>
              </label>
            </div>
          </div>
          <div className="mt-6 grid md:grid-cols-2 gap-6">
            <TokenList label="Service Areas (states)" items={profile.service_areas} onAdd={(v) => addTo("service_areas", v.toUpperCase())} onRemove={(i) => removeFrom("service_areas", i)} placeholder="e.g. NC, SC, GA" testid="areas" />
            <TokenList label="Equipment" items={profile.equipment} onAdd={(v) => addTo("equipment", v)} onRemove={(i) => removeFrom("equipment", i)} placeholder="e.g. Bobcat T770" testid="equipment" />
          </div>
        </Section>

        <Section title="Narrative">
          <div className="mono-label text-slate-400 mb-2">Capabilities & Experience</div>
          <textarea value={profile.capabilities || ""} onChange={(e) => set("capabilities", e.target.value)} rows={6} placeholder="Describe your company's core capabilities, differentiators, and experience…" className="w-full bg-[#0D1424] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none leading-relaxed" data-testid="input-capabilities" />
        </Section>

        <PersonnelEditor
          personnel={profile.key_personnel || []}
          onChange={(next) => set("key_personnel", next)}
        />

        <EstimatorSettingsSection />
      </div>
    </div>
  );
}

function EstimatorSettingsSection() {
  const [tightness, setTightness] = useState(15);
  const [loaded, setLoaded] = useState(false);
  const [savingS, setSavingS] = useState(false);

  useEffect(() => {
    api.get("/workspace/estimator-settings").then(r => {
      setTightness(r.data.anchor_tightness_pct || 15);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const saveTightness = async () => {
    setSavingS(true);
    try {
      const clamped = Math.max(5, Math.min(50, Number(tightness) || 15));
      const r = await api.put("/workspace/estimator-settings", { anchor_tightness_pct: clamped });
      setTightness(r.data.anchor_tightness_pct);
      toast.success(`Anchor tightness set to ±${r.data.anchor_tightness_pct}%`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed — owner/admin role required");
    } finally { setSavingS(false); }
  };

  if (!loaded) return null;
  return (
    <div className="bg-[#0D1424] border border-white/10 p-6" data-testid="estimator-settings">
      <div className="mono-label text-cyan-400 mb-4">Estimator Anchor Tightness</div>
      <p className="text-sm text-slate-400 mb-4">
        How tight the estimator must fit the ±band around a published magnitude / peer wins band. Default <span className="font-mono text-white">15%</span>. Bump to <span className="font-mono text-white">10%</span> if your COs are strict, or relax to <span className="font-mono text-white">25%</span> if you bid aggressively above published magnitudes.
      </p>
      <div className="flex items-center gap-4">
        <input
          type="range"
          min={5}
          max={50}
          step={1}
          value={tightness}
          onChange={(e) => setTightness(Number(e.target.value))}
          className="flex-1 accent-cyan-400"
          data-testid="tightness-slider"
        />
        <div className="min-w-[80px] text-right">
          <input
            type="number"
            min={5}
            max={50}
            value={tightness}
            onChange={(e) => setTightness(Number(e.target.value))}
            className="w-16 bg-[#0D1424] border border-white/10 focus:border-cyan-400 px-2 py-1 text-sm outline-none text-center font-mono"
            data-testid="tightness-input"
          />
          <span className="text-slate-400 ml-1 font-mono text-sm">%</span>
        </div>
        <button
          onClick={saveTightness}
          disabled={savingS}
          className="bf-pill px-5 py-2 text-xs tracking-widest uppercase font-display font-bold inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="save-tightness-btn"
        >
          {savingS ? "SAVING…" : <><Save className="w-3.5 h-3.5" />SAVE</>}
        </button>
      </div>
    </div>
  );
}

function PersonnelEditor({ personnel, onChange }) {
  const rows = Array.isArray(personnel) ? personnel : [];

  const isSsho = (role = "") => /ssho|site safety|health officer/i.test(role);
  const isQcm = (role = "") => /qcm|quality control/i.test(role);
  const hasSsho = rows.some((p) => isSsho(p?.role) && (p?.name || "").trim());
  const hasQcm = rows.some((p) => isQcm(p?.role) && (p?.name || "").trim());

  const update = (i, patch) => {
    const next = rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    onChange(next);
  };
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  const add = (preset = {}) =>
    onChange([
      ...rows,
      { name: "", role: "", phone: "", email: "", responsibilities: "", certifications: [], ...preset },
    ]);

  return (
    <div className="bg-[#0D1424] border border-white/10 p-6" data-testid="personnel-editor">
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="mono-label text-cyan-400 mb-1">Key Personnel</div>
          <p className="text-sm text-slate-400">
            Named signatories the AI drops into your binder team table. Federal construction binders require an <span className="text-white font-mono">SSHO</span> (and typically a <span className="text-white font-mono">QCM</span>) — exports are blocked without them.
          </p>
        </div>
        <button
          type="button"
          onClick={() => add()}
          className="border border-white/20 hover:border-cyan-400 hover:text-cyan-400 mono-label px-3 py-2 transition-colors inline-flex items-center gap-1.5 whitespace-nowrap"
          data-testid="personnel-add"
        >
          <Plus className="w-3.5 h-3.5" /> Add Person
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mt-3 mb-5" data-testid="personnel-role-status">
        <ComplianceChip ok={hasSsho} label="SSHO" testid="chip-ssho" />
        <ComplianceChip ok={hasQcm} label="QCM" testid="chip-qcm" />
      </div>

      {rows.length === 0 ? (
        <div className="border border-dashed border-white/10 px-4 py-8 text-center text-sm text-slate-400" data-testid="personnel-empty">
          No personnel yet. Add at least a Project Manager and an SSHO to unlock federal binder exports.
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map((p, i) => (
            <PersonnelRow
              key={i}
              index={i}
              person={p}
              onUpdate={(patch) => update(i, patch)}
              onRemove={() => remove(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ComplianceChip({ ok, label, testid }) {
  return (
    <span
      className={`mono-label px-2 py-1 border inline-flex items-center gap-1.5 ${
        ok ? "border-emerald-400/40 text-emerald-400 bg-emerald-400/5" : "border-amber-400/40 text-amber-400 bg-amber-400/5"
      }`}
      data-testid={testid}
    >
      <ShieldCheck className="w-3 h-3" /> {label} {ok ? "OK" : "MISSING"}
    </span>
  );
}

function PersonnelRow({ index, person, onUpdate, onRemove }) {
  const roleValue = person.role || "";
  // Roles are stored as a slash-joined string ("Superintendent / SSHO ...") so
  // the backend prompt + PDF renderer + role-presence check all keep working
  // without a schema migration. On the UI side we split on " / " to build a
  // chip-based multi-select, and anything that doesn't match a preset falls
  // through to the custom-role field.
  const tokens = roleValue.split(" / ").map((t) => t.trim()).filter(Boolean);
  const presetSet = new Set(ROLE_OPTIONS);
  const selectedPresets = tokens.filter((t) => presetSet.has(t));
  const customTokens = tokens.filter((t) => !presetSet.has(t));
  const [customRoleInput, setCustomRoleInput] = useState("");
  const [certInput, setCertInput] = useState("");
  const certs = Array.isArray(person.certifications) ? person.certifications : [];

  const joinRoles = (presets, customs) =>
    [...presets, ...customs].filter(Boolean).join(" / ");

  const togglePreset = (r) => {
    const has = selectedPresets.includes(r);
    const nextPresets = has
      ? selectedPresets.filter((x) => x !== r)
      : [...selectedPresets, r];
    onUpdate({ role: joinRoles(nextPresets, customTokens) });
  };

  const addCustomRole = () => {
    const t = customRoleInput.trim();
    if (!t) return;
    // Dedupe against both preset selections + existing customs.
    if ([...selectedPresets, ...customTokens].some((x) => x.toLowerCase() === t.toLowerCase())) {
      setCustomRoleInput("");
      return;
    }
    onUpdate({ role: joinRoles(selectedPresets, [...customTokens, t]) });
    setCustomRoleInput("");
  };

  const removeCustomRole = (i) => {
    const next = customTokens.filter((_, idx) => idx !== i);
    onUpdate({ role: joinRoles(selectedPresets, next) });
  };

  const addCert = (v) => {
    const t = (v || "").trim();
    if (!t) return;
    onUpdate({ certifications: [...certs, t] });
    setCertInput("");
  };
  const removeCert = (i) => onUpdate({ certifications: certs.filter((_, idx) => idx !== i) });

  return (
    <div className="border border-white/10 p-4 space-y-3" data-testid={`personnel-row-${index}`}>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <div className="mono-label text-slate-400 mb-1.5">Name</div>
          <input
            value={person.name || ""}
            onChange={(e) => onUpdate({ name: e.target.value })}
            placeholder="e.g. Jane Doe"
            className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none"
            data-testid={`personnel-name-${index}`}
          />
        </div>
        <div>
          <div className="mono-label text-slate-400 mb-1.5">Phone</div>
          <input
            value={person.phone || ""}
            onChange={(e) => onUpdate({ phone: e.target.value })}
            placeholder="(555) 555-5555"
            className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none"
            data-testid={`personnel-phone-${index}`}
          />
        </div>
        <div>
          <div className="mono-label text-slate-400 mb-1.5">Email</div>
          <input
            value={person.email || ""}
            onChange={(e) => onUpdate({ email: e.target.value })}
            placeholder="name@company.com"
            className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none"
            data-testid={`personnel-email-${index}`}
          />
        </div>
      </div>

      <div>
        <div className="mono-label text-slate-400 mb-1.5">
          Role(s)
          {tokens.length > 1 && (
            <span className="ml-2 text-cyan-400 normal-case tracking-normal font-mono text-[10px]">
              · {tokens.length} SELECTED (WEARS MULTIPLE HATS)
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid={`personnel-role-picker-${index}`}>
          {ROLE_OPTIONS.map((r) => {
            const on = selectedPresets.includes(r);
            return (
              <button
                type="button"
                key={r}
                onClick={() => togglePreset(r)}
                className={`text-[11px] font-mono px-2.5 py-1.5 border transition-colors ${
                  on
                    ? "border-cyan-400 bg-cyan-400/10 text-cyan-300"
                    : "border-white/10 bg-transparent text-slate-300 hover:border-white/30 hover:text-white"
                }`}
                data-testid={`personnel-role-toggle-${index}-${r.replace(/\W+/g, "_").toLowerCase()}`}
              >
                {on ? "✓ " : "+ "}{r}
              </button>
            );
          })}
        </div>
        {customTokens.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {customTokens.map((c, ci) => (
              <span
                key={ci}
                className="text-[11px] font-mono px-2.5 py-1.5 border border-fuchsia-400/40 bg-fuchsia-400/5 text-fuchsia-300 inline-flex items-center gap-1.5"
                data-testid={`personnel-role-custom-chip-${index}-${ci}`}
              >
                {c}
                <button
                  type="button"
                  onClick={() => removeCustomRole(ci)}
                  className="text-fuchsia-300 hover:text-red-300"
                  data-testid={`personnel-role-custom-remove-${index}-${ci}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="flex gap-2 mt-2">
          <input
            value={customRoleInput}
            onChange={(e) => setCustomRoleInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomRole(); } }}
            placeholder="Custom role (e.g. Owner, Bond Agent) — press Enter to add"
            className="flex-1 bg-[#070B14] border border-white/10 focus:border-fuchsia-400 px-3 py-2 text-sm outline-none"
            data-testid={`personnel-role-custom-input-${index}`}
          />
          <button
            type="button"
            onClick={addCustomRole}
            className="border border-white/20 hover:border-fuchsia-400 hover:text-fuchsia-400 px-3 mono-label transition-colors text-xs"
            data-testid={`personnel-role-custom-add-${index}`}
          >
            Add
          </button>
        </div>
      </div>

      <div>
        <div className="mono-label text-slate-400 mb-1.5">Responsibilities (optional — AI derives from role if blank)</div>
        <textarea
          value={person.responsibilities || ""}
          onChange={(e) => onUpdate({ responsibilities: e.target.value })}
          rows={2}
          placeholder="Daily on-site supervision, safety plan enforcement, OSHA/EM 385-1-1 compliance…"
          className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none leading-relaxed"
          data-testid={`personnel-resp-${index}`}
        />
      </div>

      <div>
        <div className="mono-label text-slate-400 mb-1.5">Certifications</div>
        <div className="flex gap-2 mb-2">
          <input
            value={certInput}
            onChange={(e) => setCertInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCert(certInput); } }}
            placeholder="e.g. OSHA 30, EM 385-1-1, CHST"
            className="flex-1 bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none"
            data-testid={`personnel-cert-input-${index}`}
          />
          <button
            type="button"
            onClick={() => addCert(certInput)}
            className="border border-white/20 hover:border-cyan-400 hover:text-cyan-400 px-3 mono-label transition-colors"
            data-testid={`personnel-cert-add-${index}`}
          >
            Add
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {certs.map((c, ci) => (
            <span key={ci} className="mono-label text-cyan-400 border border-cyan-400/30 px-2 py-1 flex items-center gap-1.5">
              {c}
              <button type="button" onClick={() => removeCert(ci)} className="hover:text-red-400" data-testid={`personnel-cert-remove-${index}-${ci}`}>×</button>
            </span>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onRemove}
          className="mono-label text-red-400/80 hover:text-red-400 border border-red-400/20 hover:border-red-400/60 px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors"
          data-testid={`personnel-remove-${index}`}
        >
          <Trash2 className="w-3.5 h-3.5" /> Remove
        </button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-[#0D1424] border border-white/10 p-6">
      <div className="mono-label text-cyan-400 mb-4">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <div className="mono-label text-slate-400 mb-2">{label}</div>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full bg-[#0D1424] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid={testid} />
    </div>
  );
}
